import { Component, effect, input, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { take } from 'rxjs';
import { ApplicationUser } from '../../../models/applicationUser';
import { ApplicationUserAbstract } from '../../../models/applicationUserAbstract';
import { Client } from '../../../models/client';
import { EntityNote } from '../../../models/entityNote';
import { Judge } from '../../../models/judge';
import { LawFirm } from '../../../models/lawFirm';
import { Vendor } from '../../../models/vendor';
import { StoreEntityNoteResponse } from '../../../responses/storeEntityNoteResponse';
import { UserBasicDataResponse } from '../../../responses/userBasicDataResponse';
import { AuthenticationService } from '../../../services/AuthenticationService';

@Component({ standalone: false,
  selector: 'app-entity-notes',
  templateUrl: './entity-notes.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./entity-notes.component.scss']
})
export class EntityNotesComponent{
    readonly entity = input<Client|LawFirm|Vendor|Judge|ApplicationUser|ApplicationUserAbstract|UserBasicDataResponse|undefined>(undefined);
    readonly readOnly = input<boolean>(false);
    
    public addingNewNote:boolean = false;
    public editNoteId?:number;

    public noteForm:FormGroup;

    public notes:EntityNote[]=[];

    constructor(private authService:AuthenticationService, public snackBar: MatSnackBar) { 
        this.noteForm = new FormGroup({
            note: new FormControl<string|undefined>(undefined, Validators.required),
        });
        effect(()=>{
            if(this.entity()) this.getNotes();
        });
    }

    get canSaveNote(){
        return this.noteForm.dirty;
    }

    get canAddNote(){
        return (this.entityId || this.entityGuid) && this.editNoteId==undefined && !this.addingNewNote;
    }

    get entityType(): string {
        const value = this.entity();
        switch (true) {
            case value instanceof ApplicationUser:
            case value instanceof ApplicationUserAbstract:
            case value instanceof UserBasicDataResponse:
                return 'user';
            case value instanceof Client:
                return 'client';
            case value instanceof LawFirm:
                return 'lawfirm';
            case value instanceof Vendor:
                return 'vendor';
            case value instanceof Judge:
                return 'judge';
            default:
                return 'user'; //should never happen but we might have untyped instances...
        }
    }

    get entityId(){
        return this.entityType==='user' ? undefined : (this.entity()?.id as number);
    }

    get entityGuid(){
        return this.entityType==='user' ? (this.entity()?.id as string) : undefined;
    }

    private getNotes(){
        this.authService.getEntityNotes(this.entityType, this.entityId, this.entityGuid).pipe(take(1)).subscribe((notes_array:EntityNote[])=>{
            this.notes=notes_array.sort((a,b)=>b.id!-a.id!);
        });
    }

    public canEditNote(en:EntityNote){
        //only if author of note
        return en.userId==this.authService.currentUserValue?.id;
    }

    public isNewNote(en:EntityNote){
        return !en.id;
    }

    public onAddNote(){
        let new_en= new EntityNote();
        new_en.entityType=this.entityType;
        new_en.entityId=this.entityId;
        new_en.entityGuid=this.entityGuid;
        new_en.userId=this.authService.currentUserValue?.id;
        new_en.userFirstName=this.authService.currentUserValue?.firstName;
        new_en.userLastName=this.authService.currentUserValue?.lastName;
        this.notes.push(new_en);
        this.addingNewNote=true;
    }

    public onEditNote(en:EntityNote){
        this.noteForm.get("note")!.setValue(en.note);
        this.editNoteId=en.id;
    }

    public onSaveNote(en:EntityNote){
        //save logic here if note form dirty
        en.note = this.noteForm.get("note")!.value;
        this.authService.storeEntityNote(en).pipe(take(1)).subscribe(r => this.processStoreEntityNoteResponse(r));
    }

    private processStoreEntityNoteResponse(r:StoreEntityNoteResponse){
        if(r.success){
            this.getNotes()
            this.noteForm.reset();
            this.editNoteId=undefined;
            this.addingNewNote=false;
            this.snackBar.open('Note saved successfully!', '×', { panelClass: ['primary-snackbar'], verticalPosition: 'top', duration: 3000 });
        }
        else{
            this.snackBar.open('Saving of note failed: ' + r.message, '×', { panelClass: ['warn-snackbar'], verticalPosition: 'top'});
        }
    }

    public onCancelNote(en:EntityNote){
        //if new note (last note in array) then remove from array
        if(!en.id){
            this.notes.splice(this.notes.length-1,1);
        }
        this.editNoteId=undefined;
        this.addingNewNote=false;
        this.noteForm.reset();
    }
}